import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { APPEAL_STATUS, fmtDate } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { NewAppealDialog } from "@/components/appeals/NewAppealDialog";
import { AppealDetailDialog } from "@/components/appeals/AppealDetailDialog";

const CLOSED = ["upheld", "dismissed", "resolved"];

export const AppealRows = ({ items, onOpen }) => (
  <>
    {items.length === 0 && <p className="px-5 py-8 text-sm text-muted-foreground">No appeals or complaints recorded.</p>}
    {items.map((a) => (
      <button key={a.id} onClick={() => onOpen(a)} data-testid={`appeal-row-${a.id}`} className="flex w-full items-center justify-between gap-3 border-b px-5 py-4 text-left last:border-0 hover:bg-secondary/50">
        <div className="min-w-0">
          <div className="truncate font-semibold">{a.rider_name} · {a.kind === "appeal" ? "Appeal" : "Complaint"}</div>
          <div className="text-xs text-muted-foreground">Received {fmtDate(a.received_date)}{a.neutral_assessor_name ? ` · Neutral: ${a.neutral_assessor_name}` : ""}</div>
        </div>
        <span className={`whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${CLOSED.includes(a.status) ? "border-zinc-500/40 text-zinc-300" : "border-amber-500/40 text-amber-300"}`}>{APPEAL_STATUS[a.status]}</span>
      </button>
    ))}
  </>
);

export default function Appeals({ riderId, embedded }) {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/appeals", { params: { rider_id: riderId } }).then((r) => setItems(r.data));
  }, [riderId]);

  const changed = (a) => { setItems((x) => x.map((y) => (y.id === a.id ? a : y))); setOpen(a); };

  return (
    <div className={embedded ? "" : "space-y-8"}>
      {!embedded && (
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Confidential</p>
            <h1 className="mt-1 text-3xl font-extrabold tracking-tight sm:text-4xl">Appeals & complaints</h1>
            <p className="mt-1 text-sm text-muted-foreground">Re-tests must be conducted by a neutral assessor. Visible to Admin and Trustees only.</p>
          </div>
          {isAdmin && <button onClick={() => setCreating(true)} data-testid="new-appeal-btn" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground"><Plus className="h-4 w-4" /> Record appeal</button>}
        </div>
      )}
      <section className="overflow-hidden rounded-2xl border bg-card" data-testid="appeals-list">
        {embedded && (
          <div className="flex items-center justify-between border-b px-5 py-4">
            <h3 className="text-base font-bold">Appeals & complaints</h3>
            {isAdmin && <button onClick={() => setCreating(true)} data-testid="rider-new-appeal-btn" className="text-sm font-semibold text-primary hover:underline">+ Record</button>}
          </div>
        )}
        {items && <AppealRows items={items} onOpen={setOpen} />}
      </section>
      {creating && <NewAppealDialog riderId={riderId} onClose={() => setCreating(false)} onSaved={(a) => setItems((x) => [a, ...x])} />}
      {open && <AppealDetailDialog appeal={open} isAdmin={isAdmin} onClose={() => setOpen(null)} onChange={changed} />}
    </div>
  );
}
